import React, { useState } from 'react';
import { motion } from 'framer-motion';

export default function NewsletterSection() {
  const [userEmail, setUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Guard clause – do not submit empty values
    if (!userEmail.trim()) {
      setStatus('error');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('userEmail', userEmail.trim());

      const response = await fetch('https://readdy.ai/api/form/d67bjq6a728k8ctu1q6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      // Explicitly handle non‑2xx responses
      if (!response.ok) {
        const errorMsg = `Server responded with ${response.status}`;
        console.error(errorMsg);
        setStatus('error');
        return;
      }

      // Optional: try to parse JSON for better debugging
      try {
        await response.json();
      } catch {
        // If response is not JSON, we simply ignore it
      }

      setStatus('success');
      setUserEmail('');
    } catch (err) {
      console.error('Newsletter subscription failed:', err);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="w-14 h-14 flex items-center justify-center bg-red-500/20 rounded-xl mx-auto mb-6">
            <i className="ri-mail-send-line text-2xl text-red-400" />
          </div>

          <h3 className="text-2xl font-bold text-white mb-3">Güncel Kalın</h3>
          <p className="text-gray-400 text-sm mb-8">
            Yangın güvenliği alanındaki yenilikler, kampanyalar ve önemli bilgilendirmelerden
            haberdar olun.
          </p>

          <form
            id="newsletter-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="userEmail"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              required
              placeholder="E-posta adresiniz"
              className="flex-1 px-5 py-3.5 bg-white/10 border border-white/15 rounded-full text-white text-sm placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-7 py-3.5 bg-red-600 text-white text-sm font-semibold rounded-full hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                'Abone Ol'
              )}
            </button>
          </form>

          {status === 'success' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-green-400 text-sm mt-4"
            >
              <i className="ri-check-line mr-1" /> Başarıyla abone oldunuz!
            </motion.p>
          )}

          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm mt-4"
            >
              <i className="ri-error-warning-line mr-1" /> Bir hata oluştu, tekrar deneyin.
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
}